import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import { hideSpinnersStyle, UNITS } from './constants';
import { 
  ChevronDown, ChevronRight, Plus, Trash2, X, Check,
  Lock, User, FileText, Edit3, Send, Package, Calendar
} from 'lucide-react';

const BRAND = '#163248';

const ContractorView = ({ shareToken }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shareData, setShareData] = useState(null);
  const [template, setTemplate] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [contractorName, setContractorName] = useState('');
  const [nameSubmitted, setNameSubmitted] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  const [showAddItem, setShowAddItem] = useState(false);
  const [addToSectionIdx, setAddToSectionIdx] = useState(null);
  const [newItem, setNewItem] = useState({ position: '', unit: 'м²', qty: '', price: '' });
  const [editingItem, setEditingItem] = useState(null);
  const [editingSectionName, setEditingSectionName] = useState(null);
  
  const [showAddMaterial, setShowAddMaterial] = useState(false);
  const [newMaterial, setNewMaterial] = useState({ name: '', qty: '', unit: 'шт.' });

  useEffect(() => { loadSharedTemplate(); }, [shareToken]);

  const loadSharedTemplate = async () => {
    try {
      setLoading(true);
      const { data: share, error: shareError } = await supabase
        .from('shared_templates')
        .select('*')
        .eq('share_token', shareToken)
        .single();
      
      if (shareError || !share) {
        setError('Посилання недійсне або картку не знайдено');
        setLoading(false);
        return;
      }
      
      setShareData(share);
      setTemplate(JSON.parse(JSON.stringify(share.template_snapshot)));
      
      const expanded = {};
      (share.template_snapshot?.sections || []).forEach((_, idx) => { expanded[idx] = true; });
      setExpandedSections(expanded);
      setLoading(false);
    } catch (e) {
      console.error('Помилка завантаження:', e);
      setError('Помилка завантаження даних');
      setLoading(false);
    }
  };

  const toggleSection = (idx) => setExpandedSections(prev => ({ ...prev, [idx]: !prev[idx] }));

  const updateItem = (sectionIdx, itemIdx, field, value) => {
    setTemplate(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      if (field === 'qty' || field === 'price') {
        updated.sections[sectionIdx].items[itemIdx][field] = value === '' ? '' : parseFloat(value) || 0;
      } else {
        updated.sections[sectionIdx].items[itemIdx][field] = value;
      }
      return updated;
    });
  };

  const addItem = () => {
    if (!newItem.position.trim()) return;
    setTemplate(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      const section = updated.sections[addToSectionIdx];
      if (!section.items) section.items = [];
      section.items.push({
        id: Date.now(), position: newItem.position.trim(), unit: newItem.unit,
        qty: parseFloat(newItem.qty) || 0, price: parseFloat(newItem.price) || 0,
        type: 'Робота', addedByContractor: true
      });
      return updated;
    });
    setNewItem({ position: '', unit: 'м²', qty: '', price: '' });
    setShowAddItem(false);
  };

  const deleteItem = (sectionIdx, itemIdx) => {
    setTemplate(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      updated.sections[sectionIdx].items.splice(itemIdx, 1);
      return updated;
    });
  };

  const updateSectionName = (sectionIdx, name) => {
    setTemplate(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      updated.sections[sectionIdx].name = name;
      return updated;
    });
  };

  const deleteSection = (sectionIdx) => {
    setTemplate(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      updated.sections.splice(sectionIdx, 1);
      return updated;
    });
  };

  const addSection = () => {
    setTemplate(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      updated.sections.push({ id: Date.now(), name: 'Новий розділ', items: [], addedByContractor: true });
      setExpandedSections(p => ({ ...p, [updated.sections.length - 1]: true }));
      return updated;
    });
  };

  const addMaterial = () => {
    if (!newMaterial.name.trim()) return;
    setMaterials(prev => [...prev, { id: Date.now(), name: newMaterial.name.trim(), qty: parseFloat(newMaterial.qty) || 0, unit: newMaterial.unit }]);
    setNewMaterial({ name: '', qty: '', unit: 'шт.' });
    setShowAddMaterial(false);
  };

  const deleteMaterial = (idx) => setMaterials(prev => prev.filter((_, i) => i !== idx));

  const updateMaterial = (idx, field, value) => {
    setMaterials(prev => prev.map((m, i) => {
      if (i !== idx) return m;
      if (field === 'qty') return { ...m, qty: value === '' ? '' : parseFloat(value) || 0 };
      return { ...m, [field]: value };
    }));
  };

  const calcSectionTotal = (section) => (section.items || []).reduce((sum, item) => sum + ((item.qty || 0) * (item.price || 0)), 0);
  const calcGrandTotal = () => template?.sections ? template.sections.reduce((sum, section) => sum + calcSectionTotal(section), 0) : 0;

  const handleSubmit = async () => {
    setSending(true);
    try {
      const { error } = await supabase
        .from('contract_responses')
        .insert({
          shared_template_id: shareData.id,
          contractor_name: contractorName.trim(),
          contractor_data: { ...template, materials, startDate: startDate || null, endDate: endDate || null }
        });
      if (error) throw error;
      setSent(true);
    } catch (e) {
      console.error('Помилка відправки:', e);
      alert('Помилка відправки. Спробуйте ще раз.');
    }
    setSending(false);
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <style>{hideSpinnersStyle}</style>
      <div className="text-center">
        <div className="w-10 h-10 border-3 border-gray-300 border-t-gray-600 rounded-full animate-spin mx-auto mb-3"></div>
        <p className="text-sm text-gray-500">Завантаження...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <style>{hideSpinnersStyle}</style>
      <div className="text-center max-w-md mx-auto px-6">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Lock className="w-7 h-7 text-gray-400" />
        </div>
        <h1 className="text-lg font-semibold text-gray-900 mb-2">Посилання недійсне</h1>
        <p className="text-sm text-gray-500">{error}</p>
      </div>
    </div>
  );

  if (!template) return null;

  if (!nameSubmitted) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <style>{hideSpinnersStyle}</style>
      <div className="bg-white rounded-2xl shadow-lg max-w-sm w-full p-6">
        <div className="text-center mb-6">
          <span className="logo-text text-xl">LIHTAR</span>
          <p className="text-xs text-gray-400 mt-1">Картка тендеру</p>
        </div>
        <div className="bg-gray-50 rounded-xl p-4 mb-6">
          <h2 className="font-semibold text-base" style={{ color: BRAND }}>{template.name}</h2>
          {template.description && <p className="text-sm text-gray-500 mt-1">{template.description}</p>}
          <div className="flex gap-3 mt-2 text-xs text-gray-400">
            <span>{template.sections?.length || 0} розділів</span>
            <span>{template.sections?.reduce((s, sec) => s + (sec.items?.length || 0), 0) || 0} позицій</span>
          </div>
        </div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Ваше ім'я або назва бригади</label>
        <input type="text" value={contractorName} onChange={(e) => {
            let val = e.target.value;
            if (val.length === 1) val = val.toUpperCase();
            else if (val.length > 1 && val[0] !== val[0].toUpperCase()) val = val[0].toUpperCase() + val.slice(1);
            setContractorName(val);
          }}
          placeholder="Наприклад: Бригада Петренко"
          className="w-full px-4 py-3 border rounded-xl text-sm focus:outline-none focus:ring-2 mb-4"
          style={{ borderColor: '#e5e7eb' }} autoFocus
          onKeyDown={(e) => { if (e.key === 'Enter' && contractorName.trim()) setNameSubmitted(true); }}
        />
        <button onClick={() => contractorName.trim() && setNameSubmitted(true)} disabled={!contractorName.trim()}
          className="w-full py-3 rounded-xl text-white font-semibold text-sm transition-opacity"
          style={{ backgroundColor: BRAND, opacity: contractorName.trim() ? 1 : 0.4 }}>
          Продовжити
        </button>
      </div>
    </div>
  );

  if (sent) return (
    <div className="min-h-screen bg-gray-50">
      <style>{hideSpinnersStyle}</style>
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-2">
          <span className="logo-text text-lg">LIHTAR</span>
          <span className="text-xs text-gray-400 hidden sm:inline">• картка тендеру</span>
        </div>
      </div>
      <div className="max-w-2xl mx-auto px-4 py-8 text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Check className="w-8 h-8 text-green-600" />
        </div>
        <h1 className="text-xl font-bold mb-2" style={{ color: BRAND }}>Пропозицію відправлено</h1>
        <p className="text-sm text-gray-500 mb-6">{contractorName && `${contractorName} • `}{template.name}</p>
        <div className="bg-white rounded-xl border shadow-sm p-5 text-left max-w-md mx-auto">
          <h3 className="text-sm font-semibold mb-3" style={{ color: BRAND }}>Роботи</h3>
          {template.sections?.map((section, sIdx) => (
            <div key={section.id || sIdx} className="flex justify-between text-sm py-1.5 border-b border-gray-100">
              <span className="text-gray-600">{section.name}</span>
              <span className="font-medium tabular-nums">{calcSectionTotal(section).toLocaleString('uk-UA', { maximumFractionDigits: 0 })} ₴</span>
            </div>
          ))}
          <div className="flex justify-between pt-2 mt-1">
            <span className="font-semibold" style={{ color: BRAND }}>Разом:</span>
            <span className="font-bold text-lg" style={{ color: BRAND }}>{calcGrandTotal().toLocaleString('uk-UA', { maximumFractionDigits: 0 })} ₴</span>
          </div>
          {materials.length > 0 && (
            <>
              <h3 className="text-sm font-semibold mt-4 mb-2" style={{ color: '#f59e0b' }}>Матеріали</h3>
              {materials.map((m, idx) => (
                <div key={m.id || idx} className="flex justify-between text-sm py-1 border-b border-gray-100">
                  <span className="text-gray-600">{m.name}</span>
                  <span className="text-gray-500">{m.qty || 0} {m.unit || ''}</span>
                </div>
              ))}
            </>
          )}
          {(startDate || endDate) && (
            <>
              <h3 className="text-sm font-semibold mt-4 mb-2" style={{ color: '#8b5cf6' }}>Терміни</h3>
              <div className="flex gap-4 text-sm">
                {startDate && <div><span className="text-gray-500">Початок: </span><span style={{ color: BRAND }}>{new Date(startDate).toLocaleDateString('uk-UA', { day: 'numeric', month: 'short', year: 'numeric' })}</span></div>}
                {endDate && <div><span className="text-gray-500">Кінець: </span><span style={{ color: BRAND }}>{new Date(endDate).toLocaleDateString('uk-UA', { day: 'numeric', month: 'short', year: 'numeric' })}</span></div>}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  const grandTotal = calcGrandTotal();

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <style>{hideSpinnersStyle}</style>
      
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="logo-text text-lg">LIHTAR</span>
            <span className="text-xs text-gray-400 hidden sm:inline">• картка тендеру</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <User className="w-4 h-4" /><span>{contractorName}</span>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-4 space-y-3">
        <div className="bg-white rounded-xl border shadow-sm p-4">
          <h1 className="text-lg font-bold" style={{ color: BRAND }}>{template.name}</h1>
          {template.description && <p className="text-sm text-gray-500 mt-1">{template.description}</p>}
          <p className="text-xs text-gray-400 mt-2">Заповніть ціни та об'єми робіт. Вкажіть необхідні матеріали.</p>
        </div>

        {/* РОБОТИ */}
        <div className="flex items-center gap-2 mt-2 mb-1">
          <FileText className="w-4 h-4" style={{ color: BRAND }} />
          <span className="text-sm font-semibold uppercase tracking-wide" style={{ color: BRAND }}>Роботи</span>
        </div>

        {template.sections?.map((section, sIdx) => {
          const isOpen = expandedSections[sIdx] !== false;
          const sectionTotal = calcSectionTotal(section);
          return (
            <div key={section.id || sIdx} className="bg-white rounded-xl border shadow-sm overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 cursor-pointer active:bg-gray-50" onClick={() => toggleSection(sIdx)}>
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  {isOpen ? <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                  {editingSectionName === sIdx ? (
                    <input type="text" value={section.name} onChange={(e) => updateSectionName(sIdx, e.target.value)}
                      className="font-semibold text-sm bg-gray-100 px-2 py-1 rounded-lg focus:outline-none focus:ring-2 flex-1"
                      style={{ color: BRAND }} autoFocus onClick={(e) => e.stopPropagation()}
                      onBlur={() => setEditingSectionName(null)} onKeyDown={(e) => { if (e.key === 'Enter') setEditingSectionName(null); }}
                    />
                  ) : (
                    <span className="font-semibold text-sm truncate cursor-text" style={{ color: BRAND }}
                      onClick={(e) => { e.stopPropagation(); setEditingSectionName(sIdx); }}>
                      {sIdx + 1}. {section.name}
                    </span>
                  )}
                  <span className="text-xs text-gray-400">({(section.items || []).length})</span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-sm font-semibold tabular-nums" style={{ color: BRAND }}>
                    {sectionTotal > 0 ? `${sectionTotal.toLocaleString('uk-UA', { maximumFractionDigits: 0 })} ₴` : '—'}
                  </span>
                  <button onClick={(e) => { e.stopPropagation(); deleteSection(sIdx); }} className="p-1 hover:bg-red-50 rounded transition-colors">
                    <Trash2 className="w-3.5 h-3.5 text-gray-300 hover:text-red-500" />
                  </button>
                </div>
              </div>

              {isOpen && (
                <div>
                  {(section.items || []).map((item, iIdx) => (
                    <div key={item.id || iIdx} className="border-t px-4 py-3">
                      <div className="flex items-start gap-2">
                        <div className="flex-1 min-w-0">
                          {editingItem?.sectionIdx === sIdx && editingItem?.itemIdx === iIdx ? (
                            <input type="text" value={item.position} onChange={(e) => updateItem(sIdx, iIdx, 'position', e.target.value)}
                              className="w-full text-sm font-medium px-2 py-1 border rounded-lg focus:outline-none focus:ring-2"
                              style={{ color: BRAND }} autoFocus onBlur={() => setEditingItem(null)}
                              onKeyDown={(e) => e.key === 'Enter' && setEditingItem(null)}
                            />
                          ) : (
                            <p className="text-sm font-medium cursor-pointer hover:bg-gray-50 px-2 py-1 -mx-2 rounded"
                              style={{ color: BRAND }} onClick={() => setEditingItem({ sectionIdx: sIdx, itemIdx: iIdx })}>
                              {item.position || 'Без назви'}
                              {item.addedByContractor && <span className="ml-2 text-[9px] bg-blue-100 text-blue-600 px-1 py-0.5 rounded font-bold">+</span>}
                            </p>
                          )}
                        </div>
                        {item.addedByContractor && (
                          <button onClick={() => deleteItem(sIdx, iIdx)} className="p-1 hover:bg-red-50 rounded transition-colors flex-shrink-0">
                            <Trash2 className="w-3.5 h-3.5 text-gray-400 hover:text-red-500" />
                          </button>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <div className="flex-1">
                          <label className="text-[10px] text-gray-400 uppercase">Об'єм</label>
                          <input type="number" value={item.qty || ''} onChange={(e) => updateItem(sIdx, iIdx, 'qty', e.target.value)}
                            placeholder="0" className="w-full px-2 py-1.5 border rounded-lg text-sm tabular-nums focus:outline-none focus:ring-2"
                            style={{ borderColor: '#e5e7eb' }} inputMode="decimal" />
                        </div>
                        <div className="w-20">
                          <label className="text-[10px] text-gray-400 uppercase">Од.</label>
                          <select value={item.unit || 'м²'} onChange={(e) => updateItem(sIdx, iIdx, 'unit', e.target.value)}
                            className="w-full px-1 py-1.5 border rounded-lg text-sm focus:outline-none focus:ring-2"
                            style={{ borderColor: '#e5e7eb' }}>
                            {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                          </select>
                        </div>
                        <div className="flex-1">
                          <label className="text-[10px] text-gray-400 uppercase">Ціна, ₴</label>
                          <input type="number" value={item.price || ''} onChange={(e) => updateItem(sIdx, iIdx, 'price', e.target.value)}
                            placeholder="0" className="w-full px-2 py-1.5 border rounded-lg text-sm tabular-nums focus:outline-none focus:ring-2"
                            style={{ borderColor: '#e5e7eb' }} inputMode="decimal" />
                        </div>
                        <div className="w-20 text-right">
                          <label className="text-[10px] text-gray-400 uppercase">Сума</label>
                          <p className="text-sm font-semibold tabular-nums py-1.5" style={{ color: BRAND }}>
                            {((item.qty || 0) * (item.price || 0)).toLocaleString('uk-UA', { maximumFractionDigits: 0 })} ₴
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                  <div className="border-t px-4 py-2">
                    <button onClick={() => { setAddToSectionIdx(sIdx); setNewItem({ position: '', unit: 'м²', qty: '', price: '' }); setShowAddItem(true); }}
                      className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-blue-600 transition-colors py-1">
                      <Plus className="w-4 h-4" /><span>Додати позицію</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        <button onClick={addSection}
          className="w-full py-3 border-2 border-dashed border-gray-300 rounded-xl text-sm text-gray-500 hover:border-gray-400 hover:text-gray-600 transition-colors flex items-center justify-center gap-2">
          <Plus className="w-4 h-4" />Додати розділ
        </button>

        {/* МАТЕРІАЛИ */}
        <div className="flex items-center gap-2 mt-6 mb-1">
          <Package className="w-4 h-4" style={{ color: '#f59e0b' }} />
          <span className="text-sm font-semibold uppercase tracking-wide" style={{ color: '#f59e0b' }}>Матеріали</span>
          <span className="text-xs text-gray-400">({materials.length})</span>
        </div>

        <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
          {materials.length === 0 ? (
            <div className="text-center py-6 text-gray-400 text-sm">Вкажіть необхідні матеріали для робіт</div>
          ) : (
            <div>
              {materials.map((m, idx) => (
                <div key={m.id || idx} className="flex items-center gap-2 px-4 py-3 border-b border-gray-100">
                  <div className="flex-1 min-w-0">
                    <input type="text" value={m.name} onChange={(e) => {
                        let val = e.target.value;
                        if (val.length > 0) val = val[0].toUpperCase() + val.slice(1);
                        updateMaterial(idx, 'name', val);
                      }}
                      className="w-full text-sm font-medium bg-transparent focus:bg-gray-50 px-2 py-1 rounded focus:outline-none focus:ring-2"
                      style={{ color: BRAND }} placeholder="Назва матеріалу" />
                  </div>
                  <div className="w-16">
                    <input type="number" value={m.qty || ''} onChange={(e) => updateMaterial(idx, 'qty', e.target.value)}
                      className="w-full text-sm text-center px-1 py-1 border rounded-lg focus:outline-none focus:ring-2"
                      style={{ borderColor: '#e5e7eb' }} placeholder="0" inputMode="decimal" />
                  </div>
                  <div className="w-16">
                    <select value={m.unit || 'шт.'} onChange={(e) => updateMaterial(idx, 'unit', e.target.value)}
                      className="w-full text-sm px-1 py-1 border rounded-lg focus:outline-none focus:ring-2"
                      style={{ borderColor: '#e5e7eb' }}>
                      {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                    </select>
                  </div>
                  <button onClick={() => deleteMaterial(idx)} className="p-1 hover:bg-red-50 rounded transition-colors flex-shrink-0">
                    <Trash2 className="w-3.5 h-3.5 text-gray-300 hover:text-red-500" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="px-4 py-2 border-t">
            <button onClick={() => { setNewMaterial({ name: '', qty: '' }); setShowAddMaterial(true); }}
              className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-amber-600 transition-colors py-1">
              <Plus className="w-4 h-4" /><span>Додати матеріал</span>
            </button>
          </div>
        </div>

        {/* ТЕРМІНИ */}
        <div className="flex items-center gap-2 mt-6 mb-1">
          <Calendar className="w-4 h-4" style={{ color: '#8b5cf6' }} />
          <span className="text-sm font-semibold uppercase tracking-wide" style={{ color: '#8b5cf6' }}>Терміни</span>
        </div>

        <div className="bg-white rounded-xl border shadow-sm p-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-gray-500 mb-1.5 block">Готовий приступити з</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2"
                style={{ borderColor: '#e5e7eb', color: BRAND }}
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1.5 block">Закінчу до</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate || undefined}
                className="w-full px-3 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2"
                style={{ borderColor: '#e5e7eb', color: BRAND }}
              />
            </div>
          </div>
          {startDate && endDate && (() => {
            const days = Math.ceil((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24));
            return days > 0 ? (
              <p className="text-xs text-gray-400 mt-2 text-center">{days} днів</p>
            ) : null;
          })()}
        </div>
      </div>

      {/* Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg z-20">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <div>
            <p className="text-xs text-gray-400">Загальна сума робіт</p>
            <p className="text-lg font-bold tabular-nums" style={{ color: BRAND }}>
              {grandTotal.toLocaleString('uk-UA', { maximumFractionDigits: 0 })} ₴
            </p>
          </div>
          <button onClick={handleSubmit} disabled={sending || grandTotal === 0}
            className="flex items-center gap-2 px-6 py-3 rounded-xl text-white font-semibold text-sm transition-all"
            style={{ backgroundColor: BRAND, opacity: (sending || grandTotal === 0) ? 0.4 : 1 }}>
            {sending ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Send className="w-4 h-4" />}
            {sending ? 'Відправка...' : 'Відправити'}
          </button>
        </div>
      </div>

      {/* Модалка додавання позиції */}
      {showAddItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold" style={{ color: BRAND }}>Нова позиція</h3>
              <button onClick={() => setShowAddItem(false)} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-500" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Назва роботи</label>
                <input type="text" value={newItem.position} onChange={(e) => { let val = e.target.value; if (val.length > 0) val = val[0].toUpperCase() + val.slice(1); setNewItem(p => ({ ...p, position: val })); }}
                  placeholder="Наприклад: Ґрунтування стін" className="w-full px-3 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2"
                  style={{ borderColor: '#e5e7eb' }} autoFocus />
              </div>
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="text-xs text-gray-500 mb-1 block">Об'єм</label>
                  <input type="number" value={newItem.qty} onChange={(e) => setNewItem(p => ({ ...p, qty: e.target.value }))}
                    placeholder="0" className="w-full px-3 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2"
                    style={{ borderColor: '#e5e7eb' }} inputMode="decimal" />
                </div>
                <div className="w-24">
                  <label className="text-xs text-gray-500 mb-1 block">Одиниця</label>
                  <select value={newItem.unit} onChange={(e) => setNewItem(p => ({ ...p, unit: e.target.value }))}
                    className="w-full px-2 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2" style={{ borderColor: '#e5e7eb' }}>
                    {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
                <div className="flex-1">
                  <label className="text-xs text-gray-500 mb-1 block">Ціна, ₴</label>
                  <input type="number" value={newItem.price} onChange={(e) => setNewItem(p => ({ ...p, price: e.target.value }))}
                    placeholder="0" className="w-full px-3 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2"
                    style={{ borderColor: '#e5e7eb' }} inputMode="decimal" />
                </div>
              </div>
            </div>
            <button onClick={addItem} disabled={!newItem.position.trim()}
              className="w-full mt-4 py-3 rounded-xl text-white font-semibold text-sm"
              style={{ backgroundColor: BRAND, opacity: newItem.position.trim() ? 1 : 0.4 }}>Додати</button>
          </div>
        </div>
      )}

      {/* Модалка додавання матеріалу */}
      {showAddMaterial && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold" style={{ color: '#f59e0b' }}>Новий матеріал</h3>
              <button onClick={() => setShowAddMaterial(false)} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-500" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Назва матеріалу</label>
                <input type="text" value={newMaterial.name} onChange={(e) => { let val = e.target.value; if (val.length > 0) val = val[0].toUpperCase() + val.slice(1); setNewMaterial(p => ({ ...p, name: val })); }}
                  placeholder="Наприклад: Ceresit CT 17" className="w-full px-3 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2"
                  style={{ borderColor: '#e5e7eb' }} autoFocus />
              </div>
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="text-xs text-gray-500 mb-1 block">Кількість</label>
                  <input type="number" value={newMaterial.qty} onChange={(e) => setNewMaterial(p => ({ ...p, qty: e.target.value }))}
                    placeholder="0" className="w-full px-3 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2"
                    style={{ borderColor: '#e5e7eb' }} inputMode="decimal" />
                </div>
                <div className="w-24">
                  <label className="text-xs text-gray-500 mb-1 block">Одиниця</label>
                  <select value={newMaterial.unit} onChange={(e) => setNewMaterial(p => ({ ...p, unit: e.target.value }))}
                    className="w-full px-2 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2"
                    style={{ borderColor: '#e5e7eb' }}>
                    {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
              </div>
            </div>
            <button onClick={addMaterial} disabled={!newMaterial.name.trim()}
              className="w-full mt-4 py-3 rounded-xl text-white font-semibold text-sm"
              style={{ backgroundColor: '#f59e0b', opacity: newMaterial.name.trim() ? 1 : 0.4 }}>Додати</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractorView;
